import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';

export interface CommunitySession {
  id: string;
  title: string;
  category_id: string;
  content: string;
  audio_url: string | null;
  duration: number;
  session_type: string;
  tags: string[];
  background_music_file: string | null;
  background_audio_url?: string | null; // Full URL to background music (single source of truth)
  background_music_volume_db: number | null;
  is_anonymous: boolean;
  submitted_by_name: string | null;
  content_warning: string | null;
  is_primary: boolean;
  created_at: string;
  approved_at: string | null;
  approval_status: 'pending' | 'approved' | 'rejected' | 'user_revoked' | 'admin_revoked';
  revoked_at: string | null;
  revoked_by_user_id: string | null;
  revocation_reason: string | null;
  // Social features (Migration 025)
  like_count: number;
  comment_count: number;
}

// Query keys for cache management - exported for use in other hooks
export const communitySessionsKeys = {
  all: ['community-sessions'] as const,
  list: (categoryId?: string, featuredOnly?: boolean) =>
    [...communitySessionsKeys.all, 'list', { categoryId, featuredOnly }] as const,
};

export const useCommunitySessionsHook = (categoryId?: string, featuredOnly: boolean = false) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: communitySessions = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: communitySessionsKeys.list(categoryId, featuredOnly),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (categoryId) params.append('category_id', categoryId);
      if (featuredOnly) params.append('featured_only', 'true');
      params.append('limit', '100');

      const url = `/community-sessions/approved${params.toString() ? `?${params.toString()}` : ''}`;
      return apiClient.get<CommunitySession[]>(url);
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Show error toast when query fails
  useEffect(() => {
    if (error) {
      const errorMessage = (error as Error).message || 'Failed to fetch community sessions';
      console.error('Error fetching community sessions:', error);
      toast({
        title: "Error Loading Community Sessions",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  return {
    communitySessions,
    loading,
    error: error ? (error as Error).message : null,
    refetch,
  };
};

/**
 * Helper to update a session's engagement counts in the cache.
 * Call this from like/comment mutations to keep counts in sync.
 */
export const updateSessionEngagement = (
  queryClient: ReturnType<typeof useQueryClient>,
  sessionId: string,
  updates: { like_count?: number; comment_count?: number }
) => {
  // Update all community session list caches that might contain this session
  queryClient.setQueriesData<CommunitySession[]>(
    { queryKey: communitySessionsKeys.all },
    (oldData) => {
      if (!oldData) return oldData;
      return oldData.map((session) =>
        session.id === sessionId
          ? { ...session, ...updates }
          : session
      );
    }
  );
};
